// chat/voiceTools.ts — THE SEAM between a live voice call and everything this app can do.
//
// Why this file exists (2026-08-13). Bogdan: "the power to amend entries, add, create,
// remove folders, change stuff inside the app — through chat AND voice." Until now the
// voice call carried three hardcoded tools (save_lesson / list_library / read_library_item)
// that touched saved notes only, so a call could never change the app's own data.
//
// The app already has both halves of the machine:
//   • CHAT_ACTIONS  — the catalogue of everything it can do (sent to the chat brain)
//   • executeAction — the ONE executor that performs any of those actions
// A voice tool call is therefore just: name + args → ChatAction → executeAction. Voice
// inherits exactly the powers chat has, and there is no second implementation to drift.
//
// ⚠️ WHY A SEPARATE FILE INSTEAD OF CALLING toAction DIRECTLY: every app shapes toAction
// differently — `toAction(r: ServerChat)` here, `toAction(name, params, reply, userText)`
// in Journal, returning a wrapper in some apps and a bare ChatAction in others. That
// variation is exactly what broke the fleet codemod. `runVoiceTool` is a FIXED signature
// each app implements in its own shape, so the shared live-session code never has to know.
//
// SAFETY: the model is told to ask out loud before anything destructive, but a spoken
// "yes" is not a security boundary — the model can mishear. So destructive verbs are
// gated HERE too: the device refuses unless the call carried an explicit confirmation.
import { libraryAdd, libraryList, libraryFind, libraryRemove, libraryMove, libraryMerge } from "../library";
import { toAction } from "./brain";
import { executeAction } from "./actions";
import { getAccessToken } from "../supabase";

const APP_ID = "ielts_speaking_coach";

export interface VoiceToolResult {
  ok: boolean;
  /** Sent back to the model on the socket, and shown in the call transcript. */
  message: string;
  /** True when the app's own data changed, so the screen behind the call can refresh. */
  changed?: boolean;
}

/** Verbs that must never run on a mishearing. Mirrors the server's DESTRUCTIVE_RE. */
const DESTRUCTIVE = /^(delete|remove|merge|clear|reset|restore|import|wipe)|_(delete|remove|merge|restore)$/i;

/** The model must pass confirmed:true, which it is instructed to set only after a spoken yes. */
const isConfirmed = (a: Record<string, unknown>): boolean =>
  a["confirmed"] === true || String(a["confirmed"] ?? "").toLowerCase() === "true" ||
  String(a["confirm"] ?? "").toLowerCase() === "yes";

const str = (v: unknown): string | undefined =>
  typeof v === "string" && v.trim() ? v.trim() : undefined;

/**
 * Run ONE in-call tool. `name` is an action from this app's CHAT_ACTIONS catalogue.
 * Never throws — a live call must not die because a tool failed.
 */
export async function runVoiceTool(name: string, args: Record<string, unknown>, customerSaid = "", sessionId = ""): Promise<VoiceToolResult> {
  try {
    // cloud_* verbs are enforced on the SERVER against the customer's own transcribed words.
    // Do NOT gate them on a model-written param here: that is the fictitious-consent pattern the
    // security review caught in chat-brain (2026-08-13), and it would also block a legitimate
    // spoken "yes" whenever the model simply omitted the flag.
    if (!name.startsWith("cloud_") && DESTRUCTIVE.test(name) && !isConfirmed(args)) {
      return { ok: false, message: "Not done yet — say clearly that you want this, and I'll do it." };
    }

    switch (name) {
      // ── the customer's own library (device-only, every Oriora app) ──────────
      case "library_save":
      case "save_lesson": {                                   // legacy name, older tickets
        const content = str(args["content"]);
        if (!content) return { ok: false, message: "Tell me what to save and I'll keep it." };
        const rawKind = (str(args["item_kind"]) ?? "lesson").toLowerCase();
        const kind = rawKind === "plan" || rawKind === "note" ? rawKind : "lesson";
        const item = libraryAdd(str(args["title"]) ?? "Untitled", kind, content, str(args["folder"]) ?? "");
        return { ok: true, message: `Saved "${item.title}" to your library.`, changed: true };
      }
      case "library_list":
      case "list_library": {
        const items = libraryList(str(args["folder"]));
        if (!items.length) return { ok: true, message: "Your library is empty." };
        return { ok: true, message: `${items.length} saved: ${items.map((i) => i.title).join(", ")}.` };
      }
      case "read_library_item": {
        const hits = libraryFind(str(args["title"]) ?? "");
        if (hits.length === 1) return { ok: true, message: hits[0].content };
        return { ok: false, message: hits.length ? "Several match — which one?" : "I couldn't find that." };
      }
      case "library_remove": {
        const hits = libraryFind(str(args["title"]) ?? "");
        if (hits.length !== 1) {
          return { ok: false, message: hits.length ? "Several match — which one?" : "I couldn't find that." };
        }
        libraryRemove(hits[0].id);
        return { ok: true, message: `Removed "${hits[0].title}".`, changed: true };
      }
      case "library_move": {
        const folder = str(args["folder"]);
        if (!folder) return { ok: false, message: "Which folder?" };
        const hits = libraryFind(str(args["title"]) ?? "");
        if (hits.length !== 1) {
          return { ok: false, message: hits.length ? "Several match — which one?" : "I couldn't find that." };
        }
        libraryMove(hits[0].id, folder);
        return { ok: true, message: `Moved "${hits[0].title}" into ${folder}.`, changed: true };
      }
      case "library_merge": {
        const titles = (str(args["titles"]) ?? "").split("|").map((t) => t.trim()).filter(Boolean);
        if (titles.length < 2) return { ok: false, message: "Which two should I merge?" };
        const ids: string[] = [];
        for (const t of titles) {
          const hits = libraryFind(t);
          if (hits.length !== 1) return { ok: false, message: `I couldn't pin down "${t}".` };
          ids.push(hits[0].id);
        }
        const merged = libraryMerge(ids, str(args["new_title"]) ?? titles.join(" + "));
        return merged
          ? { ok: true, message: `Merged into "${merged.title}".`, changed: true }
          : { ok: false, message: "That merge didn't work." };
      }

      default: {
        // ── EVERYTHING ELSE the app can do. Route through the app's OWN brain and
        // executor — the identical path a typed message takes — so a voice call has the
        // same reach as the chat and there is no second implementation to drift.
        const r = await toAction({ action: name, params: args, reply: "" });
        const acts = r.actions.filter((a) => a.kind !== "none");
        if (!acts.length) {
          const msg = r.actions[0] && "message" in r.actions[0] ? String((r.actions[0] as { message?: string }).message ?? "") : "";
          return { ok: false, message: msg || `I can't do "${name}" from a call yet.` };
        }
        const lines: string[] = [];
        let changed = false;
        for (const a of acts) {
          const res = await executeAction(a);
          lines.push(res.message);
          if (res.changed) changed = true;
        }
        // The REAL outcome, never the model's claim.
        return { ok: true, message: lines.filter(Boolean).join(" "), changed };
      }
    }
  } catch (err) {
    return { ok: false, message: `That didn't work: ${String(err)}` };
  }
}

/** Tools this app cannot run itself go to the SERVER, which holds the customer's cloud
 *  credential. Deliberately GENERIC: every future server-side power reaches live voice through
 *  this same path with no further app release — the property the typed chat already has. */
async function forwardToServer(name: string, args: Record<string, unknown>, customerSaid: string, sessionId: string): Promise<VoiceToolResult> {
  try {
    const token = await getAccessToken();
    if (!token) return { ok: false, message: "You're signed out — sign in and I can do that." };
    const r = await fetch("https://api.orioralabs.com/v1/realtime/tool", {
      method: "POST",
      headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
      // customer_said carries the customer's OWN transcribed words — the consent source for
      // destructive verbs. A model-written flag would be forgeable by anything it reads.
      body: JSON.stringify({ name, args, app_id: APP_ID, customer_said: customerSaid, session_id: sessionId }),
    });
    if (!r.ok) return { ok: false, message: `I couldn't do that (${r.status}).` };
    const j = (await r.json()) as { ok?: boolean; message?: string };
    return { ok: !!j.ok, message: j.message ?? "", changed: !!j.ok };
  } catch (err) {
    return { ok: false, message: `That didn't work: ${String(err)}` };
  }
}
